import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  Calendar,
  Car,
  Clock,
  Database,
  DollarSign,
  Edit3,
  Gauge,
  Loader2,
  Link2,
  Package,
  Plus,
  ShieldCheck,
  Search,
  TrendingUp,
  Upload,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PageHeader from '../components/workspace/PageHeader';
import MetricStrip from '../components/workspace/MetricStrip';
import WorkspacePanel from '../components/workspace/WorkspacePanel';
import StatusChip from '../components/workspace/StatusChip';
import DataTableShell from '../components/workspace/DataTableShell';
import ConfirmActionDialog from '../components/workspace/ConfirmActionDialog';
import ProgramEditDrawer from '../components/edit/ProgramEditDrawer';
import EntityEditDrawer from '../components/edit/EntityEditDrawer';
import programsApi from '../api/programs';

const tabs = [
  'Overview',
  'Parts',
  'S&OP',
  'Prices',
  'Claims / Changes',
  'Market Forecasts',
  'Activity'
];

const formatNumber = (value) => {
  const number = Number(value || 0);
  return new Intl.NumberFormat('en-US').format(number);
};

const formatMoney = (value) => {
  const number = Number(value || 0);
  if (Math.abs(number) >= 1000000) {
    return `$${(number / 1000000).toFixed(1)}M`;
  }
  if (Math.abs(number) >= 1000) {
    return `$${(number / 1000).toFixed(0)}K`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(number);
};

const formatUnitPrice = (value) => {
  if (value === null || value === undefined || value === '') return 'Not set';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(value || 0));
};

const formatDate = (value) => {
  if (!value) return 'Not set';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric'
  });
};

const formatDateTime = (value) => {
  if (!value) return 'Not set';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const statusTone = (status) => {
  const normalized = (status || '').toLowerCase();
  if (['active', 'awarded', 'production', 'sourced'].includes(normalized)) return 'green';
  if (['planning', 'target', 'rfq', 'quoted'].includes(normalized)) return 'blue';
  if (['inactive', 'lost'].includes(normalized)) return 'slate';
  return 'amber';
};

export default function ProgramDetail() {
  const { programId } = useParams();
  const navigate = useNavigate();

  const [program, setProgram] = useState(null);
  const [parts, setParts] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [forecastData, setForecastData] = useState([]);
  const [marketForecasts, setMarketForecasts] = useState([]);
  const [marketBasis, setMarketBasis] = useState([]);
  const [partMarketLinks, setPartMarketLinks] = useState([]);
  const [marketSummary, setMarketSummary] = useState(null);
  const [priceLifecycle, setPriceLifecycle] = useState({ summary: null, parts: [] });
  const [pipelineOpportunities, setPipelineOpportunities] = useState([]);
  const [activityEvents, setActivityEvents] = useState([]);
  const [granularity, setGranularity] = useState('annual');
  const [activeTab, setActiveTab] = useState('Overview');
  const [editOpen, setEditOpen] = useState(false);
  const [linkPartsOpen, setLinkPartsOpen] = useState(false);
  const [linkMarketOpen, setLinkMarketOpen] = useState(false);
  const [unlinkAction, setUnlinkAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProgramData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await programsApi.getProgramDetails(programId);
      if (response.success) {
        const responseProgram = response.data.program;
        const responseScenarios = response.data.scenarios || [];
        const activeScenario = responseScenarios.find((scenario) => scenario.is_active) || responseScenarios[0] || null;

        setProgram(responseProgram);
        setParts(response.data.parts || []);
        setScenarios(responseScenarios);
        setCurrentScenario(activeScenario);
        setPipelineOpportunities(response.data.pipeline_opportunities || []);
      } else {
        setError(response.error || 'Program not found');
      }
    } catch (err) {
      setError(err.message || 'Program not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketForecastData = async () => {
    if (!programId) return;
    try {
      const response = await programsApi.getProgramMarketForecasts(programId);
      if (response.success) {
        setMarketForecasts(response.data?.carlines || []);
        setMarketBasis(response.data?.program_basis || []);
        setPartMarketLinks(response.data?.part_links || []);
        setMarketSummary(response.data?.summary || null);
      }
    } catch (err) {
      setMarketForecasts([]);
      setMarketBasis([]);
      setPartMarketLinks([]);
      setMarketSummary(null);
    }
  };

  const fetchPriceLifecycleData = async (scenarioId = null) => {
    if (!programId) return;
    try {
      const response = await programsApi.getProgramPriceLifecycle(programId, scenarioId);
      if (response.success) {
        setPriceLifecycle({
          summary: response.data?.summary || null,
          parts: response.data?.parts || []
        });
      }
    } catch (err) {
      setPriceLifecycle({ summary: null, parts: [] });
    }
  };

  const fetchActivityData = async () => {
    if (!programId) return;
    try {
      const response = await programsApi.getProgramActivity(programId);
      if (response.success) {
        setActivityEvents(response.data?.events || []);
      }
    } catch (err) {
      setActivityEvents([]);
    }
  };

  const refreshProgramWorkspace = async () => {
    await fetchProgramData();
    await fetchMarketForecastData();
    await fetchPriceLifecycleData(currentScenario?.id);
    await fetchActivityData();
  };

  useEffect(() => {
    fetchProgramData();
  }, [programId]);

  useEffect(() => {
    fetchMarketForecastData();
  }, [programId]);

  useEffect(() => {
    fetchActivityData();
  }, [programId]);

  useEffect(() => {
    fetchPriceLifecycleData(currentScenario?.id);
  }, [programId, currentScenario]);

  useEffect(() => {
    const fetchProgramForecast = async () => {
      if (!programId) return;
      try {
        const response = await programsApi.getProgramForecast(
          programId,
          currentScenario?.id,
          null,
          granularity
        );
        if (response.success) {
          setForecastData(response.data.forecasts || []);
        }
      } catch (err) {
        setForecastData([]);
      }
    };

    fetchProgramForecast();
  }, [programId, currentScenario, granularity]);

  const forecastSummary = useMemo(() => {
    return forecastData.reduce((summary, row) => {
      const volume = Number(row.volume_plan || 0);
      const price = Number(row.price_plan || 0);
      const revenue = row.revenue_plan != null ? Number(row.revenue_plan || 0) : (volume * price);
      return {
        volume: summary.volume + volume,
        revenue: summary.revenue + revenue,
        rows: summary.rows + 1
      };
    }, { volume: 0, revenue: 0, rows: 0 });
  }, [forecastData]);

  const avgPrice = forecastSummary.volume > 0
    ? forecastSummary.revenue / forecastSummary.volume
    : 0;
  const basisLabel = priceLifecycle?.summary?.basis_label || program?.basis_label || '2027 Plan';
  const annualProgramVolume = Number(priceLifecycle?.summary?.planned_volume ?? program?.total_annual_volume ?? 0);
  const annualProgramValue = Number(priceLifecycle?.summary?.covered_revenue ?? program?.total_annual_value ?? 0);
  const annualProgramPrice = annualProgramVolume > 0 && annualProgramValue > 0
    ? annualProgramValue / annualProgramVolume
    : avgPrice;
  const linkedMarketVolume = Number(marketSummary?.total_volume || 0);
  const basisCount = Number(marketSummary?.basis_count ?? marketBasis.length ?? 0);
  const appliedForecastCount = Number(marketSummary?.applied_count ?? partMarketLinks.length ?? 0);
  const priceCoverage = Number(priceLifecycle?.summary?.coverage_percent || 0);

  const metrics = [
    {
      label: 'Linked Parts',
      value: formatNumber(parts.length),
      detail: 'Program part records',
      icon: Package,
      tone: 'blue'
    },
    {
      label: 'Planned Volume',
      value: formatNumber(annualProgramVolume),
      detail: basisLabel,
      icon: BarChart3,
      tone: 'green'
    },
    {
      label: 'Annual Value',
      value: formatMoney(annualProgramValue),
      detail: `${basisLabel} revenue`,
      icon: DollarSign,
      tone: 'green'
    },
    {
      label: 'Average Price',
      value: annualProgramPrice ? `$${annualProgramPrice.toFixed(2)}` : 'Not set',
      detail: 'Annual value / volume',
      icon: TrendingUp,
      tone: 'purple'
    },
    {
      label: 'Planning Rows',
      value: formatNumber(forecastSummary.rows),
      detail: 'Forecast rows loaded',
      icon: Database,
      tone: forecastSummary.rows ? 'blue' : 'amber'
    }
  ];

  const latestUpdated = new Date().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
  const latestActivity = activityEvents[0];

  const executeUnlinkAction = async () => {
    if (!unlinkAction) return;
    try {
      if (unlinkAction.type === 'basis') {
        await programsApi.unlinkMarketForecastBasisFromProgram(program.id, unlinkAction.coreMnemonic);
        toast.success('Program forecast basis removed');
      } else {
        await programsApi.unlinkMarketForecastFromProgramParts(program.id, unlinkAction.coreMnemonic, unlinkAction.partIds || []);
        toast.success('Forecast basis removed from parts');
      }
      setUnlinkAction(null);
      await refreshProgramWorkspace();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to remove market forecast link');
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-slate-500">
        Loading Program 360...
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-red-600">
        {error || 'Program not found'}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1600px] space-y-5">
      <PageHeader
        eyebrow="Program 360"
        title={program.name}
        description="Track the commercial lifecycle across pipeline, linked parts, market forecast basis, S&OP planning, prices, claims, and approvals."
        meta={latestActivity
          ? `Last changed ${formatDateTime(latestActivity.created_at)} by ${latestActivity.created_by_name || latestActivity.created_by_email || 'workspace user'}`
          : `Last refreshed ${latestUpdated}`
        }
        actions={
          <>
            <Button variant="outline" onClick={() => navigate('/pipeline')}>
              <ArrowLeft className="h-4 w-4" />
              Pipeline
            </Button>
            <Button variant="outline" onClick={() => setEditOpen(true)}>
              <Edit3 className="h-4 w-4" />
              Edit Program
            </Button>
            <Button variant="outline" onClick={() => setLinkMarketOpen(true)}>
              <Link2 className="h-4 w-4" />
              Link Market Forecast
            </Button>
            <Button variant="outline" onClick={() => navigate('/import')}>
              <Upload className="h-4 w-4" />
              Import Programs / Parts
            </Button>
            <Button onClick={() => navigate('/planning')}>
              Open S&OP Planning
            </Button>
          </>
        }
      />

      <ProgramEditDrawer
        open={editOpen}
        onOpenChange={setEditOpen}
        program={program}
        onSaved={refreshProgramWorkspace}
      />

      <ProgramPartsDrawer
        open={linkPartsOpen}
        onOpenChange={setLinkPartsOpen}
        program={program}
        onSaved={refreshProgramWorkspace}
      />

      <MarketForecastLinkDrawer
        open={linkMarketOpen}
        onOpenChange={setLinkMarketOpen}
        program={program}
        parts={parts}
        onSaved={refreshProgramWorkspace}
      />

      <ConfirmActionDialog
        open={Boolean(unlinkAction)}
        onOpenChange={(open) => !open && setUnlinkAction(null)}
        title={unlinkAction?.type === 'basis' ? 'Remove Program forecast basis?' : 'Remove forecast from parts?'}
        description={unlinkAction?.type === 'basis'
          ? 'This removes the Program-level market forecast basis. Part-applied links are not removed by this action.'
          : 'This removes the selected forecast basis from linked parts. Program-level basis remains available.'
        }
        items={unlinkAction?.items || []}
        confirmLabel="Remove link"
        destructive
        onConfirm={executeUnlinkAction}
      />

      <WorkspacePanel bodyClassName="p-0">
        <div className="grid gap-0 lg:grid-cols-[1.35fr_1fr]">
          <div className="border-b border-slate-200 p-5 lg:border-b-0 lg:border-r">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusChip tone={statusTone(program.status)}>{program.status || 'Active'}</StatusChip>
                  <StatusChip tone="blue">{program.oem || 'Customer not set'}</StatusChip>
                  {program.platform && <StatusChip tone="slate">{program.platform}</StatusChip>}
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <ContextItem icon={Car} label="Platform / Nameplate" value={program.platform || program.code || 'Not set'} />
                  <ContextItem icon={Calendar} label="SOP" value={formatDate(program.start_date)} />
                  <ContextItem icon={Calendar} label="EOP" value={formatDate(program.end_date)} />
                  <ContextItem icon={Users} label="Customer" value={program.oem || 'Not set'} />
                </div>
              </div>
            </div>
          </div>
          <div className="p-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Planning Basis</div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <CompactFact label="Active Scenario" value={currentScenario?.name || 'Baseline'} />
              <CompactFact label="Annual Basis" value={basisLabel} />
              <CompactFact label="Market Source" value={basisCount ? `${basisCount} IHS/S&P basis forecast${basisCount === 1 ? '' : 's'}` : 'Not linked yet'} />
              <CompactFact label="Price Source" value={forecastSummary.rows ? 'S&OP weighted price' : 'Needs price rows'} />
            </div>
          </div>
        </div>
      </WorkspacePanel>

      <MetricStrip metrics={metrics} columns="lg:grid-cols-5" />

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              activeTab === tab
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-950'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'Overview' && (
        <div className="space-y-5">
          <div className="grid gap-5 xl:grid-cols-[1.25fr_0.75fr]">
            <WorkspacePanel
              title="Commercial Context"
              description="Program-level facts that should stay consistent across pipeline, parts, planning, and price lifecycle."
            >
              <div className="grid gap-4 md:grid-cols-2">
                <ContextItem icon={Gauge} label="Lifecycle State" value={program.status || 'Active'} />
                <ContextItem icon={Package} label="Linked Parts" value={`${parts.length} active parts`} />
                <ContextItem icon={Database} label="Market Forecast Basis" value={basisCount ? `${formatNumber(linkedMarketVolume)} lifecycle units; ${formatNumber(priceLifecycle?.summary?.market_volume || 0)} in ${basisLabel}.` : 'Link vehicle forecast identifiers before parts are finalized.'} />
                <ContextItem icon={DollarSign} label="Price Coverage" value={`${priceCoverage.toFixed(1)}% of linked parts have a usable price basis.`} />
              </div>
            </WorkspacePanel>

            <WorkspacePanel
              title="Attention Required"
              description="Setup and data-quality checks for this Program 360 record."
            >
              <div className="space-y-3 text-sm">
                <AttentionRow tone={parts.length ? 'green' : 'amber'} title="Program structure" detail={parts.length ? `${parts.length} linked part records are available.` : 'Add or import parts to make this program operational.'} />
                <AttentionRow tone={basisCount ? 'green' : 'amber'} title="Market forecast basis" detail={basisCount ? `${basisCount} vehicle forecast basis record${basisCount === 1 ? '' : 's'} linked to Program 360.` : 'Link IHS/S&P Mobility vehicle forecasts before relying on S&OP volume.'} />
                <AttentionRow tone={appliedForecastCount ? 'green' : 'amber'} title="Forecast applied to parts" detail={appliedForecastCount ? `${appliedForecastCount} forecast record${appliedForecastCount === 1 ? '' : 's'} applied to linked parts.` : 'Apply the market basis to parts once content, PPV, and take rate are known.'} />
                <AttentionRow tone={forecastSummary.rows ? 'green' : 'amber'} title="S&OP planning" detail={forecastSummary.rows ? `${forecastSummary.rows} forecast rows loaded.` : 'No forecast rows returned for selected grain.'} />
                <AttentionRow tone={priceCoverage > 0 ? 'green' : 'amber'} title="Price lifecycle" detail={priceCoverage > 0 ? `${priceCoverage.toFixed(1)}% price coverage from linked parts.` : 'Add target, quoted, awarded, or planned prices before treating this program as price-complete.'} />
              </div>
            </WorkspacePanel>
          </div>

          <WorkspacePanel
            title="Acquisition Pipeline Connection"
            description="Pipeline pursuits explicitly connected to this Program 360 record."
            actions={(
              <Button size="sm" variant="outline" onClick={() => navigate('/pipeline')}>
                Open Acquisition Pipeline
              </Button>
            )}
          >
            {pipelineOpportunities.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                No pipeline pursuit is connected yet. Open Acquisition Pipeline and create or connect Program 360 from the opportunity drawer.
              </div>
            ) : (
              <DataTableShell>
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Pursuit</th>
                      <th className="px-3 py-2 text-left font-semibold">Stage</th>
                      <th className="px-3 py-2 text-right font-semibold">Revenue</th>
                      <th className="px-3 py-2 text-right font-semibold">Confidence</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {pipelineOpportunities.map((opportunity) => (
                      <tr key={opportunity.id}>
                        <td className="px-3 py-2 font-medium text-slate-900">{opportunity.program_name}</td>
                        <td className="px-3 py-2"><StatusChip tone={statusTone(opportunity.stage)}>{opportunity.stage}</StatusChip></td>
                        <td className="px-3 py-2 text-right">{formatMoney((opportunity.revenue_potential_cents || 0) / 100)}</td>
                        <td className="px-3 py-2 text-right">{opportunity.confidence_percent ?? 'Not set'}{opportunity.confidence_percent !== null && opportunity.confidence_percent !== undefined ? '%' : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </DataTableShell>
            )}
          </WorkspacePanel>
        </div>
      )}

      {activeTab === 'Parts' && (
        <WorkspacePanel
          title={`Linked Parts (${parts.length})`}
          description="Parts inherit program context and feed S&OP, price lifecycle, claims, and market forecast impact."
          actions={(
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => navigate('/import')}>
                <Upload className="h-3.5 w-3.5" />
                Import Parts
              </Button>
              <Button size="sm" variant="outline" onClick={() => setLinkPartsOpen(true)}>
                <Plus className="h-3.5 w-3.5" />
                Add / Link Parts
              </Button>
            </div>
          )}
        >
          <PartsTable
            parts={parts}
            partMarketLinks={partMarketLinks}
            onOpenPart={(part) => navigate(`/parts/${part.part_number}?programId=${program.id}`)}
          />
        </WorkspacePanel>
      )}

      {activeTab === 'S&OP' && (
        <WorkspacePanel
          title="S&OP Forecast"
          description={`${basisLabel} is the representative annual basis. Market volume becomes supplier volume through take rate, content per vehicle, and adjustments.`}
          actions={
            <div className="flex items-center gap-2">
              <select
                value={currentScenario?.id || ''}
                onChange={(event) => {
                  const selected = scenarios.find((scenario) => scenario.id === event.target.value);
                  setCurrentScenario(selected || null);
                }}
                className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
              >
                {scenarios.length === 0 ? (
                  <option value="">Baseline</option>
                ) : (
                  scenarios.map((scenario) => (
                    <option key={scenario.id} value={scenario.id}>
                      {scenario.name}{scenario.is_active ? ' (active)' : ''}
                    </option>
                  ))
                )}
              </select>
              <select
                value={granularity}
                onChange={(event) => setGranularity(event.target.value)}
                className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
              >
                <option value="annual">Annual</option>
                <option value="quarterly">Quarterly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          }
        >
          <ForecastTable rows={forecastData} granularity={granularity} onImport={() => navigate('/import')} onOpenMarket={() => setLinkMarketOpen(true)} />
        </WorkspacePanel>
      )}

      {activeTab === 'Prices' && (
        <WorkspacePanel
          title="Program Price Lifecycle"
          description="Commercial price coverage rolled up from linked parts, awarded or quoted prices, and S&OP planned price rows."
        >
          <PriceLifecyclePanel
            lifecycle={priceLifecycle}
            onOpenPart={(part) => navigate(`/parts/${part.part_number}?programId=${program.id}`)}
          />
        </WorkspacePanel>
      )}

      {activeTab === 'Claims / Changes' && (
        <WorkspacePanel
            title="Claims And Change Impact"
            description="Financial recovery and engineering changes should attach to affected parts and update price lifecycle."
          >
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            Claim and change impact is tracked through affected parts, financial recovery, approval status, and activity history. Open Claims / Recovery for the detailed record workflow.
          </div>
        </WorkspacePanel>
      )}

      {activeTab === 'Market Forecasts' && (
        <WorkspacePanel
          title="Market Forecast Basis"
          description="Linked IHS/S&P Mobility vehicle forecasts should drive the first S&OP row before supplier take rate, PPV, allocation, and adjustments."
          actions={(
            <Button size="sm" variant="outline" onClick={() => setLinkMarketOpen(true)}>
              <Link2 className="h-3.5 w-3.5" />
              Link Market Forecast
            </Button>
          )}
        >
          <MarketForecastTable
            carlines={marketForecasts}
            programBasis={marketBasis}
            partLinks={partMarketLinks}
            summary={marketSummary}
            onOpenMarket={() => setLinkMarketOpen(true)}
            onUnlinkBasis={(row) => setUnlinkAction({
              type: 'basis',
              coreMnemonic: row.core_mnemonic,
              items: [
                `${row.program_name || row.nameplate || row.core_mnemonic} will be removed from Program forecast basis.`,
                'Part-applied market forecast links will stay in place.'
              ]
            })}
            onUnlinkParts={(row) => setUnlinkAction({
              type: 'parts',
              coreMnemonic: row.core_mnemonic,
              partIds: (row.linked_parts || []).map((part) => part.id),
              items: [
                `${row.program_name || row.nameplate || row.core_mnemonic} will be removed from ${(row.linked_parts || []).length} linked part${(row.linked_parts || []).length === 1 ? '' : 's'}.`,
                'The Program forecast basis will remain available for later re-application.'
              ]
            })}
          />
        </WorkspacePanel>
      )}

      {activeTab === 'Activity' && (
        <WorkspacePanel
          title="Program Activity"
          description="Saved Program 360 changes and committed workflow actions."
        >
          <ActivityTable events={activityEvents} />
        </WorkspacePanel>
      )}
    </div>
  );
}

function ActivityTable({ events }) {
  if (!events.length) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
        <div className="text-sm font-medium text-slate-950">No saved Program 360 activity yet.</div>
        <p className="mx-auto mt-1 max-w-xl text-xs text-slate-500">
          Program edits, market forecast links, part links, and Pipeline promotions will appear here after they are saved.
        </p>
      </div>
    );
  }

  return (
    <DataTableShell>
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">When</th>
            <th className="px-3 py-2 text-left font-semibold">Action</th>
            <th className="px-3 py-2 text-left font-semibold">Summary</th>
            <th className="px-3 py-2 text-left font-semibold">User</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {events.map((event) => (
            <tr key={event.id} className="hover:bg-slate-50">
              <td className="px-3 py-2 text-slate-600">
                <div className="flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-slate-400" />
                  {formatDateTime(event.created_at)}
                </div>
              </td>
              <td className="px-3 py-2">
                <StatusChip tone={eventTone(event.event_type)}>{formatEventType(event.event_type)}</StatusChip>
              </td>
              <td className="px-3 py-2 font-medium text-slate-950">{event.summary}</td>
              <td className="px-3 py-2 text-slate-600">{event.created_by_name || event.created_by_email || 'Workspace user'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </DataTableShell>
  );
}

function formatEventType(value) {
  return String(value || 'activity')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function eventTone(value) {
  if (String(value || '').includes('unlink') || String(value || '').includes('unapplied')) return 'amber';
  if (String(value || '').includes('created') || String(value || '').includes('linked') || String(value || '').includes('promoted')) return 'green';
  return 'blue';
}

function ProgramPartsDrawer({ open, onOpenChange, program, onSaved }) {
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [contentPerVehicle, setContentPerVehicle] = useState('1');
  const [takeRate, setTakeRate] = useState('100');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open || !program?.id) return;
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await programsApi.getProgramPartCandidates(program.id, {
          search,
          limit: 50
        });
        if (response.success === false) {
          throw new Error(response.error || 'Unable to load parts');
        }
        setCandidates(response.data?.parts || []);
      } catch (err) {
        setCandidates([]);
        setError(err.response?.data?.detail || err.message || 'Unable to load parts');
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [open, program?.id, search]);

  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set());
    setContentPerVehicle('1');
    setTakeRate('100');
    setError(null);
  }, [open]);

  const togglePart = (partId) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(partId)) {
        next.delete(partId);
      } else {
        next.add(partId);
      }
      return next;
    });
  };

  const validateSelection = () => {
    if (selectedIds.size === 0) {
      throw new Error('Select at least one part to link');
    }
    const cpv = Number(contentPerVehicle);
    const takeRateRatio = Number(takeRate) / 100;
    if (!Number.isFinite(cpv) || cpv <= 0) {
      throw new Error('Content per vehicle must be greater than zero');
    }
    if (!Number.isFinite(takeRateRatio) || takeRateRatio < 0 || takeRateRatio > 1) {
      throw new Error('Take rate must be between 0% and 100%');
    }
    return { cpv, takeRateRatio };
  };

  const handleSave = () => {
    setError(null);
    try {
      validateSelection();
      setConfirmOpen(true);
    } catch (err) {
      setError(err.message || 'Unable to link parts');
    }
  };

  const executeLinkParts = async () => {
    setSaving(true);
    setError(null);
    try {
      const { cpv, takeRateRatio } = validateSelection();

      const response = await programsApi.linkPartsToProgram(program.id, {
        part_ids: Array.from(selectedIds),
        content_per_vehicle: cpv,
        take_rate: takeRateRatio
      });
      if (response.success === false) {
        throw new Error(response.error || response.detail || 'Unable to link parts');
      }

      await onSaved?.();
      toast.success(response.data?.message || 'Parts linked to Program 360');
      setConfirmOpen(false);
      onOpenChange(false);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Unable to link parts');
    } finally {
      setSaving(false);
    }
  };

  const selectedPartLabels = candidates
    .filter((part) => selectedIds.has(part.id))
    .map((part) => part.part_number);
  const hasChanges = selectedIds.size > 0 || contentPerVehicle !== '1' || takeRate !== '100';

  return (
    <>
    <EntityEditDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Add / Link Parts"
      description="Bring existing part records into this program and set the planning assumptions that turn vehicle volume into supplier volume."
      onSave={handleSave}
      saving={saving}
      error={error}
      saveLabel="Link selected parts"
      hasChanges={hasChanges}
      changedCount={selectedIds.size}
      saveDisabled={selectedIds.size === 0}
      onDiscard={() => {
        setSelectedIds(new Set());
        setContentPerVehicle('1');
        setTakeRate('100');
        setError(null);
      }}
    >
      <div className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block font-medium text-slate-700">Content per vehicle</span>
            <Input value={contentPerVehicle} onChange={(event) => setContentPerVehicle(event.target.value)} type="number" min="0.01" step="0.01" />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium text-slate-700">Take rate %</span>
            <Input value={takeRate} onChange={(event) => setTakeRate(event.target.value)} type="number" min="0" max="100" step="0.1" />
          </label>
        </div>

        <div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search part number, description, OEM, HTS..."
              className="pl-9"
            />
          </div>
          <p className="mt-2 text-xs text-slate-500">
            For large customer setup, bulk import remains the normal path. Use this drawer for one-off corrections and controlled Program 360 linking.
          </p>
        </div>

        <div className="max-h-[52vh] overflow-y-auto rounded-lg border border-slate-200">
          {loading ? (
            <div className="flex items-center justify-center gap-2 p-8 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading parts...
            </div>
          ) : candidates.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              No part records matched. Use import for new part creation, then link them here.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {candidates.map((part) => {
                const isSelected = selectedIds.has(part.id);
                return (
                  <label key={part.id} className={`flex cursor-pointer gap-3 p-3 hover:bg-blue-50/60 ${part.linked ? 'bg-slate-50' : 'bg-white'}`}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={part.linked}
                      onChange={() => togglePart(part.id)}
                      className="mt-1 h-4 w-4 rounded border-slate-300"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-slate-950">{part.part_number}</span>
                        {part.linked && <StatusChip tone="green">Linked</StatusChip>}
                        {part.business_status && <StatusChip tone="slate">{part.business_status}</StatusChip>}
                      </div>
                      <div className="mt-1 text-sm text-slate-600">{part.description || 'No description'}</div>
                      <div className="mt-1 text-xs text-slate-500">
                        {[part.oem, part.carline, part.production_country, part.hts_code].filter(Boolean).join(' - ') || 'No additional metadata'}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </EntityEditDrawer>
    <ConfirmActionDialog
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
      title="Link selected parts to Program 360?"
      description="This commits the selected part records to this program and sets the planning assumptions used by S&OP."
      items={[
        `${selectedIds.size} part${selectedIds.size === 1 ? '' : 's'} will be linked or updated.`,
        `Content per vehicle will be set to ${contentPerVehicle}.`,
        `Take rate will be set to ${takeRate}%.`,
        selectedPartLabels.length ? `Selected parts: ${selectedPartLabels.join(', ')}` : 'Selected part records will inherit this Program 360 context.'
      ]}
      confirmLabel="Link parts"
      onConfirm={executeLinkParts}
    />
    </>
  );
}

function MarketForecastLinkDrawer({ open, onOpenChange, program, parts, onSaved }) {
  const [search, setSearch] = useState('');
  const [forecasts, setForecasts] = useState([]);
  const [selectedForecast, setSelectedForecast] = useState(null);
  const [selectedPartIds, setSelectedPartIds] = useState(new Set());
  const [linkMode, setLinkMode] = useState('basis');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSearch(program?.oem || '');
    setSelectedForecast(null);
    setSelectedPartIds(new Set((parts || []).map((part) => part.id)));
    setLinkMode((parts || []).length ? 'basis' : 'basis');
    setError(null);
  }, [open, program?.oem, parts]);

  useEffect(() => {
    if (!open) return;
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await programsApi.searchMarketForecasts({
          search,
          limit: 25
        });
        if (response.success === false) {
          throw new Error(response.error || 'Unable to load market forecasts');
        }
        setForecasts(response.data || []);
      } catch (err) {
        setForecasts([]);
        setError(err.response?.data?.detail || err.message || 'Unable to load market forecasts');
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [open, search]);

  const togglePart = (partId) => {
    setSelectedPartIds((current) => {
      const next = new Set(current);
      if (next.has(partId)) {
        next.delete(partId);
      } else {
        next.add(partId);
      }
      return next;
    });
  };

  const validateMarketForecast = () => {
    if (!selectedForecast) {
      throw new Error('Select a market forecast record');
    }
    if (linkMode === 'parts' && selectedPartIds.size === 0) {
      throw new Error('Select at least one linked part');
    }
  };

  const handleSave = () => {
    setError(null);
    try {
      validateMarketForecast();
      setConfirmOpen(true);
    } catch (err) {
      setError(err.message || 'Unable to link market forecast');
    }
  };

  const executeMarketForecastLink = async () => {
    setSaving(true);
    setError(null);
    try {
      validateMarketForecast();

      const response = linkMode === 'parts'
        ? await programsApi.linkMarketForecastToProgramParts(program.id, {
            core_mnemonic: selectedForecast.core_mnemonic,
            part_ids: Array.from(selectedPartIds)
          })
        : await programsApi.linkMarketForecastBasisToProgram(program.id, {
            core_mnemonic: selectedForecast.core_mnemonic
          });
      if (response.success === false) {
        throw new Error(response.error || response.detail || 'Unable to link market forecast');
      }

      await onSaved?.();
      if (linkMode === 'basis') {
        const coreMnemonic = selectedForecast.core_mnemonic;
        toast.success(response.data?.message || 'Market forecast basis linked', {
          action: {
            label: 'Undo',
            onClick: async () => {
              try {
                await programsApi.unlinkMarketForecastBasisFromProgram(program.id, coreMnemonic);
                await onSaved?.();
                toast.success('Program forecast basis removed');
              } catch (undoError) {
                toast.error(undoError.response?.data?.detail || 'Unable to undo forecast basis link');
              }
            }
          }
        });
      } else {
        toast.success(response.data?.message || 'Market forecast applied to parts');
      }
      setConfirmOpen(false);
      onOpenChange(false);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Unable to link market forecast');
    } finally {
      setSaving(false);
    }
  };

  const selectedPartLabels = (parts || [])
    .filter((part) => selectedPartIds.has(part.id))
    .map((part) => part.part_number);
  const hasChanges = Boolean(selectedForecast) || linkMode !== 'basis' || selectedPartIds.size !== (parts || []).length;

  return (
    <>
    <EntityEditDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Link Market Forecast"
      description="Select an IHS/S&P Mobility vehicle forecast. Program basis captures market demand; applying to parts creates the S&OP planning basis."
      onSave={handleSave}
      saving={saving}
      error={error}
      saveLabel={linkMode === 'parts' ? 'Apply to parts' : 'Set as basis'}
      hasChanges={hasChanges}
      changedCount={(selectedForecast ? 1 : 0) + (linkMode === 'parts' ? selectedPartIds.size : 0)}
      saveDisabled={!selectedForecast || (linkMode === 'parts' && selectedPartIds.size === 0)}
      onDiscard={() => {
        setSelectedForecast(null);
        setSelectedPartIds(new Set((parts || []).map((part) => part.id)));
        setLinkMode('basis');
        setError(null);
      }}
    >
      <div className="space-y-5">
        <div className="grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setLinkMode('basis')}
            className={`rounded-lg border p-3 text-left text-sm ${linkMode === 'basis' ? 'border-blue-300 bg-blue-50 text-blue-950' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
          >
            <div className="font-semibold">Set as Program Basis</div>
            <div className="mt-1 text-xs">Capture market demand before parts are loaded.</div>
          </button>
          <button
            type="button"
            onClick={() => parts.length > 0 && setLinkMode('parts')}
            disabled={parts.length === 0}
            className={`rounded-lg border p-3 text-left text-sm ${linkMode === 'parts' ? 'border-blue-300 bg-blue-50 text-blue-950' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'} ${parts.length === 0 ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            <div className="font-semibold">Apply to Parts</div>
            <div className="mt-1 text-xs">Create the S&OP basis using take rate, PPV, and content.</div>
          </button>
        </div>

        {parts.length === 0 && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950">
            No parts are linked yet. You can still set a Program forecast basis now, then apply it to parts after the BOM or part list is imported.
          </div>
        )}

        <div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search OEM, platform, nameplate, vehicle program..."
              className="pl-9"
            />
          </div>
          <p className="mt-2 text-xs text-slate-500">
            This is the market-volume backbone. Use Program basis for early planning, then apply it to parts when supplier volume assumptions are ready.
          </p>
        </div>

        <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200">
          {loading ? (
            <div className="flex items-center justify-center gap-2 p-8 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading forecasts...
            </div>
          ) : forecasts.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              No IHS/S&P Mobility records matched this search.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {forecasts.map((forecast) => {
                const isSelected = selectedForecast?.core_mnemonic === forecast.core_mnemonic;
                return (
                  <button
                    type="button"
                    key={forecast.core_mnemonic}
                    onClick={() => setSelectedForecast(forecast)}
                    className={`w-full px-3 py-3 text-left hover:bg-blue-50/70 ${isSelected ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : 'bg-white'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-semibold text-slate-950">{forecast.program_name || forecast.name_plate || 'Unnamed vehicle forecast'}</div>
                        <div className="mt-1 text-sm text-slate-600">
                          {[forecast.oem, forecast.platform, forecast.region].filter(Boolean).join(' - ') || 'No platform metadata'}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          IHS/S&P Mobility - {forecast.core_mnemonic} - SOP {forecast.sop_year || 'n/a'} / EOP {forecast.eop_year || 'n/a'}
                        </div>
                      </div>
                      <div className="text-right text-sm font-semibold text-slate-950">{formatNumber(forecast.total_volume)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <div className="mb-2 text-sm font-semibold text-slate-950">Apply to linked parts</div>
          {linkMode === 'basis' ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              This save will set the forecast as Program basis only. S&OP rows will not change until you apply the basis to linked parts.
            </div>
          ) : parts.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              No linked parts are available yet. This forecast will remain as a Program basis until parts are added.
            </div>
          ) : (
            <div className="space-y-2">
              {parts.map((part) => (
                <label key={part.id} className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-3 hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={selectedPartIds.has(part.id)}
                    onChange={() => togglePart(part.id)}
                    className="mt-1 h-4 w-4 rounded border-slate-300"
                  />
                  <div>
                    <div className="font-medium text-slate-950">{part.part_number}</div>
                    <div className="text-xs text-slate-500">
                      {part.description || 'No description'} - content {Number(part.content_per_vehicle || 0).toFixed(2)}, take rate {(Number(part.take_rate || 0) * 100).toFixed(1)}%
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </EntityEditDrawer>
    <ConfirmActionDialog
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
      title={linkMode === 'parts' ? 'Apply forecast basis to parts?' : 'Set Program forecast basis?'}
      description={linkMode === 'parts'
        ? 'This commits the market forecast to selected parts and syncs the S&OP planning basis.'
        : 'This links market demand to Program 360 without changing part-level S&OP rows yet.'
      }
      items={linkMode === 'parts'
        ? [
            `${selectedForecast?.program_name || selectedForecast?.name_plate || 'Selected forecast'} will be applied to ${selectedPartIds.size} part${selectedPartIds.size === 1 ? '' : 's'}.`,
            'S&OP basis rows may be created or updated from the linked IHS/S&P Mobility volume.',
            selectedPartLabels.length ? `Selected parts: ${selectedPartLabels.join(', ')}` : 'Selected linked parts will receive this forecast basis.'
          ]
        : [
            `${selectedForecast?.program_name || selectedForecast?.name_plate || 'Selected forecast'} will be stored as Program forecast basis.`,
            'Part-level S&OP rows will not change until the forecast is applied to parts.'
          ]
      }
      confirmLabel={linkMode === 'parts' ? 'Apply to parts' : 'Set as basis'}
      onConfirm={executeMarketForecastLink}
    />
    </>
  );
}

function ContextItem({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-1 text-sm font-medium text-slate-950">{value}</div>
    </div>
  );
}

function CompactFact({ label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-950">{value}</div>
    </div>
  );
}

function AttentionRow({ title, detail, tone }) {
  const dotTone = tone === 'green' ? 'bg-emerald-500' : tone === 'red' ? 'bg-red-500' : 'bg-amber-500';
  return (
    <div className="flex gap-3 rounded-lg border border-slate-200 p-3">
      <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${dotTone}`} />
      <div>
        <div className="font-medium text-slate-950">{title}</div>
        <div className="text-xs text-slate-500">{detail}</div>
      </div>
    </div>
  );
}

function ReadinessCard({ icon: Icon, title, value, detail }) {
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
        <Icon className="h-4 w-4 text-blue-600" />
        {title}
      </div>
      <div className="mt-2 text-xl font-semibold text-slate-950">{value}</div>
      <p className="mt-1 text-xs text-slate-500">{detail}</p>
    </div>
  );
}

function PriceLifecyclePanel({ lifecycle, onOpenPart }) {
  const summary = lifecycle.summary || {};
  const priceParts = lifecycle.parts || [];
  const coverage = Number(summary.coverage_percent || 0);

  if (priceParts.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center">
        <div className="text-sm font-medium text-slate-950">No linked parts are available for price rollup.</div>
        <p className="mx-auto mt-1 max-w-xl text-xs text-slate-500">
          Add parts to this program first. Pricing should then roll up from target, quoted, awarded, or S&OP planned prices on each part.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <ReadinessCard
          icon={DollarSign}
          title="Price Coverage"
          value={`${summary.priced_parts || 0} / ${summary.linked_parts || priceParts.length}`}
          detail={`${coverage.toFixed(1)}% of linked parts have a usable price basis.`}
        />
        <ReadinessCard
          icon={ShieldCheck}
          title="Awarded / Locked"
          value={`${summary.awarded_parts || 0} parts`}
          detail="Awarded prices should be governed and changed through claims or amendments."
        />
        <ReadinessCard
          icon={TrendingUp}
          title="Weighted Avg Price"
          value={summary.weighted_average_price ? formatUnitPrice(summary.weighted_average_price) : 'Not set'}
          detail="Weighted by planned volume for the active planning year."
        />
        <ReadinessCard
          icon={BarChart3}
          title="Covered Revenue"
          value={formatMoney(summary.covered_revenue || 0)}
          detail={`${formatNumber(summary.planned_volume || 0)} planned units covered by price rows.`}
        />
      </div>

      {summary.missing_parts > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {summary.missing_parts} linked part{summary.missing_parts === 1 ? '' : 's'} still need a target, quoted, awarded, or planned price before this program can be treated as price-complete.
        </div>
      )}

      <DataTableShell>
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Part</th>
              <th className="px-3 py-2 text-left font-semibold">Status</th>
              <th className="px-3 py-2 text-right font-semibold">Active Unit Price</th>
              <th className="px-3 py-2 text-right font-semibold">Target</th>
              <th className="px-3 py-2 text-right font-semibold">Quoted</th>
              <th className="px-3 py-2 text-right font-semibold">Awarded</th>
              <th className="px-3 py-2 text-right font-semibold">Plan Year</th>
              <th className="px-3 py-2 text-right font-semibold">Volume</th>
              <th className="px-3 py-2 text-right font-semibold">Revenue</th>
              <th className="px-3 py-2 text-right font-semibold">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {priceParts.map((part) => {
              const statusToneValue = part.lifecycle_status === 'Missing price'
                ? 'amber'
                : ['Awarded', 'Scheduled'].includes(part.lifecycle_status)
                  ? 'green'
                  : part.lifecycle_status === 'Quoted'
                    ? 'blue'
                    : 'slate';

              return (
                <tr key={part.part_id} className="hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <div className="font-semibold text-blue-700">{part.part_number}</div>
                    <div className="max-w-[280px] truncate text-xs text-slate-500">{part.description || 'No description'}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-1">
                      <StatusChip tone={statusToneValue}>{part.lifecycle_status}</StatusChip>
                      <span className="text-xs text-slate-500">{part.price_source}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-950">{formatUnitPrice(part.active_unit_price)}</td>
                  <td className="px-3 py-2 text-right text-slate-700">{formatUnitPrice(part.target_price)}</td>
                  <td className="px-3 py-2 text-right text-slate-700">{formatUnitPrice(part.quoted_price)}</td>
                  <td className="px-3 py-2 text-right text-slate-700">{formatUnitPrice(part.awarded_price)}</td>
                  <td className="px-3 py-2 text-right text-slate-700">{part.planning_year || 'Not set'}</td>
                  <td className="px-3 py-2 text-right text-slate-700">{formatNumber(part.planned_volume)}</td>
                  <td className="px-3 py-2 text-right font-medium text-slate-950">{formatMoney(part.covered_revenue ?? part.annual_revenue ?? 0)}</td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="ghost" size="sm" onClick={() => onOpenPart(part)}>
                      Open Part 360
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </DataTableShell>
    </div>
  );
}

function PartsTable({ parts, partMarketLinks = [], onOpenPart }) {
  if (parts.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center">
        <div className="text-sm font-medium text-slate-950">No linked parts are available for this program.</div>
        <p className="mt-1 text-xs text-slate-500">
          Add or import parts to connect S&OP, price lifecycle, claims, and market forecast impact.
        </p>
      </div>
    );
  }

  const appliedForecastByPart = new Map();
  partMarketLinks.forEach((forecast) => {
    (forecast.linked_parts || []).forEach((part) => {
      appliedForecastByPart.set(part.id, forecast);
    });
  });

  return (
    <DataTableShell>
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">Part</th>
            <th className="px-3 py-2 text-left font-semibold">Description</th>
            <th className="px-3 py-2 text-right font-semibold">Content / Vehicle</th>
            <th className="px-3 py-2 text-right font-semibold">Take Rate</th>
            <th className="px-3 py-2 text-right font-semibold">Plan Volume</th>
            <th className="px-3 py-2 text-right font-semibold">Plan Revenue</th>
            <th className="px-3 py-2 text-left font-semibold">Market Basis</th>
            <th className="px-3 py-2 text-left font-semibold">Production Country</th>
            <th className="px-3 py-2 text-right font-semibold">Action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {parts.map((part) => {
            const appliedForecast = appliedForecastByPart.get(part.id);
            return (
              <tr key={part.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-semibold text-blue-700">{part.part_number}</td>
                <td className="px-3 py-2 text-slate-700">{part.description || 'No description'}</td>
                <td className="px-3 py-2 text-right text-slate-700">{Number(part.content_per_vehicle || 0).toFixed(2)}</td>
                <td className="px-3 py-2 text-right text-slate-700">{(Number(part.take_rate || 0) * 100).toFixed(1)}%</td>
                <td className="px-3 py-2 text-right text-slate-700">{formatNumber(part.planned_volume)}</td>
                <td className="px-3 py-2 text-right font-medium text-slate-950">{formatMoney(part.covered_revenue ?? part.annual_revenue ?? 0)}</td>
                <td className="px-3 py-2">
                  {appliedForecast ? (
                    <div>
                      <StatusChip tone="green">Applied</StatusChip>
                      <div className="mt-1 max-w-[220px] truncate text-xs text-slate-500">{appliedForecast.program_name || appliedForecast.nameplate}</div>
                    </div>
                  ) : (
                    <StatusChip tone="amber">Not applied</StatusChip>
                  )}
                </td>
                <td className="px-3 py-2 text-slate-700">{part.production_country || 'Not set'}</td>
                <td className="px-3 py-2 text-right">
                  <Button variant="ghost" size="sm" onClick={() => onOpenPart(part)}>
                    Open Part 360
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </DataTableShell>
  );
}

function ForecastTable({ rows, granularity, onImport, onOpenMarket }) {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center">
        <div className="text-sm font-medium text-slate-950">No {granularity} S&OP rows are available yet.</div>
        <p className="mx-auto mt-1 max-w-xl text-xs text-slate-500">
          Import planning rows or link/sync market forecasts so the first planning row starts from IHS/S&P Mobility volume.
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <Button size="sm" onClick={onImport}>
            <Upload className="h-3.5 w-3.5" />
            Import Planning Rows
          </Button>
          <Button size="sm" variant="outline" onClick={onOpenMarket}>
            <Link2 className="h-3.5 w-3.5" />
            Link Market Forecast
          </Button>
        </div>
      </div>
    );
  }

  return (
    <DataTableShell>
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">Period</th>
            <th className="px-3 py-2 text-right font-semibold">Market Volume</th>
            <th className="px-3 py-2 text-right font-semibold">Allocation</th>
            <th className="px-3 py-2 text-right font-semibold">Planned Volume</th>
            <th className="px-3 py-2 text-right font-semibold">Actual Volume</th>
            <th className="px-3 py-2 text-right font-semibold">Avg Planned Price</th>
            <th className="px-3 py-2 text-right font-semibold">Revenue Basis</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {rows.map((row, index) => {
            const period = row.month
              ? `${new Date(row.year, row.month - 1).toLocaleDateString('en-US', { month: 'short' })} ${row.year}`
              : row.quarter
                ? `Q${row.quarter} ${row.year}`
              : row.year;
            const revenue = row.revenue_plan != null
              ? Number(row.revenue_plan || 0)
              : Number(row.volume_plan || 0) * Number(row.price_plan || 0);

            return (
              <tr key={`${row.year}-${row.quarter || row.month || 'annual'}-${index}`} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-medium text-slate-950">{period}</td>
                <td className="px-3 py-2 text-right text-slate-700">{formatNumber(row.market_volume)}</td>
                <td className="px-3 py-2 text-right text-slate-700">{formatNumber(row.company_allocation)}</td>
                <td className="px-3 py-2 text-right text-slate-700">{formatNumber(row.volume_plan)}</td>
                <td className="px-3 py-2 text-right text-slate-500">{formatNumber(row.volume_actual)}</td>
                <td className="px-3 py-2 text-right text-slate-700">{row.price_plan ? `$${Number(row.price_plan).toFixed(2)}` : 'Not set'}</td>
                <td className="px-3 py-2 text-right font-medium text-slate-950">{formatMoney(revenue)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </DataTableShell>
  );
}

function MarketForecastTable({ carlines, programBasis = [], partLinks = [], summary, onOpenMarket, onUnlinkBasis, onUnlinkParts }) {
  if (carlines.length === 0 && programBasis.length === 0 && partLinks.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center">
        <div className="text-sm font-medium text-slate-950">No market forecast is linked to this program yet.</div>
        <p className="mx-auto mt-1 max-w-xl text-xs text-slate-500">
          Link IHS/S&P Mobility vehicle forecasts as the Program basis now. Apply them to parts later when content, PPV, and take rate are ready.
        </p>
        <Button className="mt-4" size="sm" onClick={onOpenMarket}>
          <Link2 className="h-3.5 w-3.5" />
          Link Market Forecast
        </Button>
      </div>
    );
  }

  const annualVolumes = summary?.annual_volumes || {};
  const activeYears = Object.entries(annualVolumes)
    .filter(([, volume]) => Number(volume || 0) > 0)
    .slice(0, 5);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-3">
        <ReadinessCard
          icon={Database}
          title="Program Basis"
          value={formatNumber(summary?.basis_count ?? programBasis.length)}
          detail="Vehicle forecast records captured for Program 360."
        />
        <ReadinessCard
          icon={Package}
          title="Applied Forecasts"
          value={formatNumber(summary?.applied_count ?? partLinks.length)}
          detail="Forecast basis records already applied to parts."
        />
        <ReadinessCard
          icon={TrendingUp}
          title="Market Volume"
          value={formatNumber(summary?.total_volume || 0)}
          detail="IHS/S&P Mobility annual volume total."
        />
      </div>

      {activeYears.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Annual Market Volume</div>
          <div className="grid gap-2 sm:grid-cols-5">
            {activeYears.map(([year, volume]) => (
              <div key={year} className="rounded-md border border-slate-200 bg-white p-2">
                <div className="text-xs text-slate-500">{year}</div>
                <div className="text-sm font-semibold text-slate-950">{formatNumber(volume)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="text-sm font-semibold text-slate-950">Program Forecast Basis</div>
        <ForecastBasisTable
          rows={programBasis}
          emptyText="No Program-level basis is linked yet."
          linkedPartsLabel={(row) => row.applied_to_parts ? `${row.applied_to_parts} part${row.applied_to_parts === 1 ? '' : 's'} applied` : 'Not allocated to parts'}
          actionLabel="Remove basis"
          onAction={onUnlinkBasis}
        />
      </div>

      <div className="space-y-2">
        <div className="text-sm font-semibold text-slate-950">Applied to Parts</div>
        <ForecastBasisTable
          rows={partLinks}
          emptyText="No forecast basis has been applied to parts yet."
          linkedPartsLabel={(row) => (row.linked_parts || []).map((part) => part.part_number).join(', ') || 'Not linked'}
          actionLabel="Remove from parts"
          onAction={onUnlinkParts}
        />
      </div>
    </div>
  );
}

function ForecastBasisTable({ rows, emptyText, linkedPartsLabel, actionLabel, onAction }) {
  if (!rows.length) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
        {emptyText}
      </div>
    );
  }

  return (
      <DataTableShell>
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Vehicle Forecast</th>
              <th className="px-3 py-2 text-left font-semibold">Platform</th>
              <th className="px-3 py-2 text-left font-semibold">Region / Plant</th>
              <th className="px-3 py-2 text-left font-semibold">SOP / EOP</th>
              <th className="px-3 py-2 text-right font-semibold">Total Volume</th>
              <th className="px-3 py-2 text-left font-semibold">Linked Parts</th>
              {onAction && <th className="px-3 py-2 text-right font-semibold">Action</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {rows.map((carline) => (
              <tr key={carline.core_mnemonic} className="hover:bg-slate-50">
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-950">{carline.program_name || carline.nameplate || 'Unnamed forecast'}</div>
                  <div className="text-xs text-slate-500">{carline.source} - {carline.core_mnemonic}</div>
                </td>
                <td className="px-3 py-2 text-slate-700">{carline.platform || 'Not set'}</td>
                <td className="px-3 py-2 text-slate-700">
                  <div>{carline.region || 'Not set'}</div>
                  <div className="text-xs text-slate-500">{carline.plant || carline.country || 'Plant not set'}</div>
                </td>
                <td className="px-3 py-2 text-slate-700">{formatDate(carline.sop)} / {formatDate(carline.eop)}</td>
                <td className="px-3 py-2 text-right font-medium text-slate-950">{formatNumber(carline.total_volume)}</td>
                <td className="px-3 py-2 text-slate-700">
                  {linkedPartsLabel(carline)}
                </td>
                {onAction && (
                  <td className="px-3 py-2 text-right">
                    <Button variant="ghost" size="sm" onClick={() => onAction(carline)}>
                      {actionLabel || 'Remove'}
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </DataTableShell>
  );
}
